import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MaintenanceConfig {
  enabled: boolean;
  message: string;
  bypass_key: string;
}

const SETTINGS_KEY = "maintenance_mode";
const BYPASS_STORAGE_KEY = "maintenance_bypass";

const DEFAULTS: MaintenanceConfig = {
  enabled: false,
  message: "We're currently performing scheduled maintenance. We'll be back shortly!",
  bypass_key: "",
};

export const useMaintenanceMode = () => {
  const [config, setConfig] = useState<MaintenanceConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000));
        const fetchPromise = supabase.from("store_settings").select("value").eq("key", SETTINGS_KEY).maybeSingle();
        const { data, error }: any = await Promise.race([fetchPromise, timeoutPromise]);
        if (!error && data?.value && typeof data.value === "object") {
          setConfig({ ...DEFAULTS, ...(data.value as Partial<MaintenanceConfig>) });
        }
      } catch (error: any) {
        setConfig(DEFAULTS);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Check if user has bypass via URL param or sessionStorage
  const hasBypass = (() => {
    if (typeof window === "undefined") return false;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlKey = params.get("bypass");
      if (urlKey && config.bypass_key && urlKey === config.bypass_key) {
        sessionStorage.setItem(BYPASS_STORAGE_KEY, urlKey);
        return true;
      }
      const storedKey = sessionStorage.getItem(BYPASS_STORAGE_KEY);
      return !!(storedKey && config.bypass_key && storedKey === config.bypass_key);
    } catch (e) {
      console.warn("Session storage access denied or failed", e);
      return false;
    }
  })();

  return {
    enabled: config.enabled,
    message: config.message,
    bypassKey: config.bypass_key,
    hasBypass,
    loading,
  };
};
